import { endOfDay, startOfDay } from 'date-fns';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import {
  SelectedDateItem,
  SortOrder,
  SortType,
  TodoView,
} from 'src/@types/todo';
import { useAuthContext } from 'src/auth/useAuthContext';
import { TagItem } from 'src/components/Tags/types';
import { useTagsQuery } from 'src/pages/dashboard/TodosPage/shared/useTagsQuery';
import { useTodoUIState } from 'src/state/ui/useTodoUIState';

export type SortPayload = {
  sortBy: SortType | string;
  order: SortOrder | string;
};

export const assignedToMe = 'assigned-to-me';
export const rightMenuId = 'right-menu-id';

export type State = {
  defaultTodoList: string;
  activeTodoGroup: string;
  selectedView: TodoView;
  selectedDate: SelectedDateItem;
  columnNumber: number;
  columnDays: string[];
  showCompleted: boolean;
  selectedTodoId?: string;
  searchTerm: string;
  sortBy: SortPayload[];
  expandedSubList: boolean;
  isRightMenu: boolean;
  activeSubList: number;
  sublistHeight: number;
  sublistColsWidth: Record<string, number>;
};

const initialState = {
  defaultTodoList: '',
  activeTodoGroup: '',
  selectedView: TodoView.COLUMNS,
  selectedDate: {
    start: startOfDay(new Date()).toString(),
    end: endOfDay(new Date()).toString(),
  },
  columnNumber: 5,
  columnDays: [],
  showCompleted: true,
  rollOver: true,
  selectedTodoId: undefined,
  searchTerm: '',
  sortBy: [],
  expandedSubList: false,
  isRightMenu: false,
  activeSubList: 0,
  sublistHeight: 400,
  sublistColsWidth: {},
};

type Actions = { type: 'update'; payload: Partial<State> };

function reducer(state: State, action: Actions) {
  switch (action.type) {
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
}

type Context = State & {
  updateState: (data: Partial<State>) => void;
  tags: TagItem[];
};

const TodosContext = createContext<Context | null>(null);

export function useTodosState() {
  const context = useContext(TodosContext);

  if (!context)
    throw new Error('useTodosContext must be use inside TodosContextProvider');

  return context;
}

type Props = {
  children: React.ReactNode;
};

export function TodosContextProvider({ children }: Props) {
  const { user } = useAuthContext();

  const showCompleted =
    user?.todo?.showCompleted !== undefined ? user.todo.showCompleted : true;
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    showCompleted,
  });

  const todoId = useMemo(
    () => (state.isRightMenu ? rightMenuId : state.defaultTodoList),
    [state.isRightMenu, state.defaultTodoList],
  );

  const { tags } = useTagsQuery(state.defaultTodoList);
  const { groupState, setGroupState } = useTodoUIState(todoId);

  useEffect(() => {
    if (todoId && groupState) {
      dispatch({
        type: 'update',
        payload: {
          selectedView: groupState.viewType as TodoView,
          columnNumber: groupState.columnNumber,
          expandedSubList: groupState.expandedSubList,
          activeSubList: groupState.activeSubList,
          sublistHeight: groupState.sublistHeight,
          sublistColsWidth: groupState.sublistColsWidth,
        },
      });
    }
  }, [todoId, groupState]);

  const updateState = (data: Partial<State>) => {
    dispatch({ type: 'update', payload: data });

    if (todoId) {
      if (data.selectedView !== undefined) {
        setGroupState({ viewType: data.selectedView });
      }
      if (data.columnNumber !== undefined) {
        setGroupState({ columnNumber: data.columnNumber });
      }
      if (data.expandedSubList !== undefined) {
        setGroupState({ expandedSubList: data.expandedSubList });
      }
      if (data.sublistHeight !== undefined) {
        setGroupState({ sublistHeight: data.sublistHeight });
      }
      if (data.sublistColsWidth !== undefined) {
        setGroupState({ sublistColsWidth: data.sublistColsWidth });
      }
      setGroupState({ activeSubList: data.activeSubList || 0 });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const value = useMemo(
    () => ({
      ...state,
      updateState,
      tags,
    }),
    [state, tags],
  );

  return (
    <TodosContext.Provider value={value}>{children}</TodosContext.Provider>
  );
}
